import { useQueries } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import type { MessagePathRecord, TopicRecord } from "@/shared/domain/topics";

/**
 * A proposed signal, named as far as it currently can be.
 *
 * `topicName`/`fieldPath` fill in when the lookups land. Until then the id is
 * all there is, and it is what gets rendered — the approval card must never
 * wait on a fetch, so every field but the id is optional by design.
 */
export interface ResolvedSignal {
  messagePathId: string;
  topicName?: string;
  fieldPath?: string;
}

/** Signals sharing one topic, in the order the proposal named them. */
export interface SignalGroup {
  /** Absent for the group of signals not yet resolved. */
  topicName?: string;
  signals: ResolvedSignal[];
}

/**
 * Names for the message paths a visualizer tool proposes.
 *
 * Uses the query keys `useMessagePathById` and `useTopicById` already use, so
 * a card usually paints resolved: the model reached these ids through topic
 * tools, and the visualizer beside the chat holds the same records.
 *
 * A failed lookup stays unresolved rather than surfacing an error. The id is a
 * worse label, not a wrong one, and a request the user can still judge should
 * not be blocked over a name.
 */
export function useResolvedSignals(
  messagePathIds: readonly string[],
): ResolvedSignal[] {
  const { topics } = useDomainServices();

  const messagePathQueries = useQueries({
    queries: messagePathIds.map((messagePathId) => ({
      queryKey: ["messagePathRecordById", messagePathId],
      queryFn: ({ signal }: { signal?: AbortSignal }) =>
        topics.getMessagePathById(messagePathId, { abortSignal: signal }),
      staleTime: 60000,
    })),
  });

  const records: (MessagePathRecord | undefined)[] = messagePathQueries.map(
    (query) => query.data,
  );

  // One query per distinct topic: a call that opens gyro x/y/z names three
  // paths on one topic, and three identical requests would be two too many.
  const topicIds = [
    ...new Set(
      records
        .map((record) => record?.topic_id)
        .filter((topicId): topicId is string => topicId !== undefined),
    ),
  ];

  const topicQueries = useQueries({
    queries: topicIds.map((topicId) => ({
      queryKey: ["topicRecordById", topicId],
      queryFn: ({ signal }: { signal?: AbortSignal }) =>
        topics.getTopicById(topicId, { abortSignal: signal }),
      staleTime: 60000,
    })),
  });

  const topicNameById = new Map<string, string>();
  topicQueries.forEach((query, index) => {
    const topic: TopicRecord | undefined = query.data;
    const topicId = topicIds[index];
    if (topic !== undefined && topicId !== undefined) {
      topicNameById.set(topicId, topic.topic_name);
    }
  });

  return messagePathIds.map((messagePathId, index) => {
    const record = records[index];
    const topicId = record?.topic_id;
    return {
      messagePathId,
      topicName: topicId === undefined ? undefined : topicNameById.get(topicId),
      fieldPath: record?.message_path,
    };
  });
}

/**
 * Signals bucketed by topic, preserving proposal order.
 *
 * Groups appear in the order they were first named and signals keep their
 * order within a group, so a row does not jump position as lookups land
 * around it. Signals whose topic is not yet known share one unnamed group.
 */
export function groupSignalsByTopic(
  signals: readonly ResolvedSignal[],
): SignalGroup[] {
  const groups: SignalGroup[] = [];
  const groupByTopicName = new Map<string, SignalGroup>();
  let unresolved: SignalGroup | undefined;

  for (const signal of signals) {
    if (signal.topicName === undefined || signal.fieldPath === undefined) {
      if (unresolved === undefined) {
        unresolved = { signals: [] };
        groups.push(unresolved);
      }
      unresolved.signals.push(signal);
      continue;
    }

    let group = groupByTopicName.get(signal.topicName);
    if (group === undefined) {
      group = { topicName: signal.topicName, signals: [] };
      groupByTopicName.set(signal.topicName, group);
      groups.push(group);
    }
    group.signals.push(signal);
  }

  return groups;
}
